/**
 * Normalizes a string for analytics.
 *
 * @param value - The string to normalize.
 * @returns The normalized string. String is converted to lowercase and all whitespace is removed.
 */
export const normalizeString = (value?: string | null): string | undefined => {
  if (value === null || value === undefined) return undefined;
  return value.toLowerCase().replace(/\s+/g, '');
};

/**
 * Normalizes email addresses for analytics. Removes dots preceding the @ symbol in Gmail addresses for Meta Pixel tracking.
 *
 * @param email - The email address to normalize.
 * @param removeGmailDots - Whether to remove dots preceding the @ symbol
 * @returns The normalized email address.
 */
export const normalizeEmail = (email?: string | null, removeGmailDots = false): string | undefined => {
  const normalized = normalizeString(email);
  if (!normalized) return undefined;
  if (removeGmailDots && /@(gmail|googlemail)\.com$/.test(normalized)) {
    const [localPart, domain] = normalized.split('@');
    return `${localPart.replace(/\./g, '')}@${domain}`;
  }
  return normalized;
};

/**
 * Normalizes names for analytics. Removes punctuation from the name for Meta Pixel tracking.
 *
 * @param name - The name to normalize.
 * @param removePunctuation - Whether to remove punctuation from the name.
 * @returns The normalized name.
 */
export const normalizeName = (name?: string | null, removePunctuation = false): string | undefined => {
  if (name === null || name === undefined) return undefined;
  const trimmed = name.toLowerCase().trim();
  if (!trimmed) return undefined;
  if (removePunctuation) {
    return trimmed.replace(/[^a-z]/g, '');
  }
  return trimmed.replace(/\s+/g, ' ');
};

/**
 * Normalizes phone numbers for analytics. GTM and Meta Pixel require different formats.
 *
 * @param phone - The phone number to normalize.
 * @param format - The format to normalize the phone number to.
 * @returns The normalized phone number.
 */
export const normalizePhone = (phone?: string | null, format: 'e164' | 'digits-only' = 'digits-only'): string | undefined => {
  if (phone === null || phone === undefined) return undefined;
  const digitsWithPlus = phone.replace(/[^\d+]/g, '');
  if (format === 'e164') {
    return digitsWithPlus.startsWith('+') ? digitsWithPlus : `+${digitsWithPlus}`;
  }
  return digitsWithPlus.replace(/\+/g, '');
};
export const normalizeDateOfBirth = (dob?: string | null): string | undefined => {
  if (dob === null || dob === undefined) return undefined;
  if (/^\d{4}-\d{2}-\d{2}$/.test(dob)) {
    return dob.replace(/-/g, '');
  }
  try {
    const date = new Date(dob);
    if (Number.isNaN(date.getTime())) return undefined;
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}${month}${day}`;
  } catch (error) {
    return undefined;
  }
};
export const normalizeAddressPart = (value?: string | null): string | undefined => {
  return normalizeString(value);
};